import { AlertCircle, ArrowDown, ArrowUp, CornerDownLeft, LoaderCircle, Search, X } from "lucide-react";
import { Fragment, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import "./command-palette.css";

export type CommandPaletteItem = {
  id: string;
  title: string;
  description?: string;
  category: "Aplicativos" | "Janelas abertas" | "Área de trabalho";
  keywords?: string;
  icon?: ReactNode;
  shortcut?: string;
  onSelect: () => void | Promise<void>;
};

type Props = {
  /** Only pass commands this user can access. Actions must revalidate server authorization. */
  items: readonly CommandPaletteItem[];
  onClose: () => void;
};

const categories = [
  { label: "Todos", value: "all" },
  { label: "Aplicativos", value: "Aplicativos" },
  { label: "Janelas", value: "Janelas abertas" },
  { label: "Ações", value: "Área de trabalho" },
] as const;

/** The shell owns the shortcut and commands; this view never discovers or launches hidden apps. */
export function CommandPalette({ items, onClose }: Props) {
  const id = useId();
  const dialog = useRef<HTMLDialogElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const executing = useRef(false);
  const restoreFocus = useRef(true);
  const mounted = useRef(false);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<(typeof categories)[number]["value"]>("all");
  const [activeIndex, setActiveIndex] = useState(0);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const matches = useMemo(() => {
    const terms = normalizeSearch(query).split(/\s+/).filter(Boolean);
    return items.filter((item) => (category === "all" || category === item.category)
      && terms.every((term) => normalizeSearch(`${item.title} ${item.description ?? ""} ${item.keywords ?? ""} ${item.category}`).includes(term)));
  }, [items, query, category]);
  const selectedIndex = Math.min(activeIndex, Math.max(0, matches.length - 1));
  const selected = matches[selectedIndex];
  const activeId = selected ? `${id}-option-${selectedIndex}` : undefined;

  useEffect(() => {
    const element = dialog.current;
    const previousFocus = document.activeElement as HTMLElement | null;
    mounted.current = true;
    if (!element) return;
    if (typeof element.showModal === "function") element.showModal();
    else element.setAttribute("open", "");
    input.current?.focus();
    return () => {
      mounted.current = false;
      if (element.open && typeof element.close === "function") element.close();
      if (restoreFocus.current && previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, []);

  useEffect(() => {
    if (activeId) document.getElementById(activeId)?.scrollIntoView?.({ block: "nearest" });
  }, [activeId, query, category]);

  useEffect(() => {
    // Browsers blur a disabled input while an async command is pending.
    // Restore its keyboard target only after the field has been re-enabled.
    if (error && !pending) input.current?.focus();
  }, [error, pending]);

  async function select(item: CommandPaletteItem) {
    if (executing.current) return;
    executing.current = true;
    setPending(true);
    setError(null);
    try {
      await item.onSelect();
      if (!mounted.current) return;
      // A launch or focus command owns its new focus target. Escape still restores the trigger.
      restoreFocus.current = false;
      onClose();
    } catch (cause) {
      if (mounted.current) setError(cause instanceof Error ? cause.message : "Não foi possível executar esta ação. Tente novamente.");
    } finally {
      executing.current = false;
      if (mounted.current) setPending(false);
    }
  }

  function navigateResults(event: KeyboardEvent<HTMLInputElement>) {
    if (event.nativeEvent.isComposing || executing.current) return;
    if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key) && matches.length > 0) {
      event.preventDefault();
      const next = event.key === "Home" ? 0 : event.key === "End" ? matches.length - 1
        : (selectedIndex + (event.key === "ArrowDown" ? 1 : -1) + matches.length) % matches.length;
      setActiveIndex(next);
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (selected) void select(selected);
    }
  }

  return createPortal(
    <dialog ref={dialog} className="command-palette" aria-labelledby={`${id}-title`} aria-describedby={`${id}-hint`} aria-modal="true"
      onCancel={(event) => { event.preventDefault(); if (!executing.current) onClose(); }}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === "Escape") { event.preventDefault(); if (!executing.current) onClose(); }
      }}
      onClick={(event) => {
        if (event.target !== event.currentTarget || executing.current) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) onClose();
      }}>
      <header className="command-palette-header"><span id={`${id}-title`}>Central de comandos</span><span className="command-palette-key-hint">Ctrl / ⌘ K</span></header>
      <div className="command-palette-search">
        <Search size={22} aria-hidden="true" />
        <input ref={input} role="combobox" aria-label="Buscar aplicativos, janelas e ações" aria-autocomplete="list" aria-expanded="true" aria-controls={`${id}-results`} aria-activedescendant={activeId}
          value={query} onChange={(event) => { setQuery(event.target.value); setActiveIndex(0); setError(null); }} onKeyDown={navigateResults}
          placeholder="O que você quer fazer?" autoComplete="off" spellCheck={false} disabled={pending} />
        <button type="button" className="command-palette-close" aria-label="Fechar central de comandos" onClick={onClose} disabled={pending}><X size={19} /></button>
      </div>
      <div className="command-palette-filters" role="group" aria-label="Filtrar comandos">
        {categories.map((item) => <button type="button" key={item.value} aria-pressed={category === item.value} disabled={pending} onClick={() => { setCategory(item.value); setActiveIndex(0); setError(null); input.current?.focus(); }}>{item.label}</button>)}
      </div>
      <div className="command-palette-results" id={`${id}-results`} role="listbox" aria-label="Resultados da busca" aria-busy={pending}>
        {matches.map((item, index) => <Fragment key={item.id}>
          {(index === 0 || matches[index - 1].category !== item.category) && <div className="command-palette-category" role="presentation">{item.category}</div>}
          <button type="button" role="option" id={`${id}-option-${index}`} aria-selected={index === selectedIndex} disabled={pending}
            tabIndex={-1} className="command-palette-result" onPointerMove={() => { if (!pending) setActiveIndex(index); }} onClick={() => void select(item)}>
            <span className="command-palette-result-icon" aria-hidden="true">{item.icon ?? <CornerDownLeft size={19} />}</span>
            <span className="command-palette-result-copy"><strong>{item.title}</strong>{item.description && <small>{item.description}</small>}</span>
            {item.shortcut && <kbd>{item.shortcut}</kbd>}
            {index === selectedIndex && <CornerDownLeft className="command-palette-result-enter" size={16} aria-hidden="true" />}
          </button>
        </Fragment>)}
        {matches.length === 0 && <div className="command-palette-empty" role="presentation"><Search size={28} aria-hidden="true" /><strong>Nenhum resultado encontrado</strong><span>Tente outro termo ou selecione “Todos”.</span></div>}
      </div>
      {error && <div className="command-palette-error" role="alert"><AlertCircle size={17} /><span>{error}</span></div>}
      <footer className="command-palette-footer" id={`${id}-hint`}>
        {pending ? <span role="status"><LoaderCircle className="spin" size={15} />Executando ação…</span> : <><span><ArrowUp size={13} /><ArrowDown size={13} /> navegar <CornerDownLeft size={13} /> abrir</span><span><kbd>Esc</kbd> fechar</span></>}
      </footer>
      <span className="command-palette-announcement" role="status" aria-live="polite">{matches.length} {matches.length === 1 ? "resultado" : "resultados"}</span>
    </dialog>, document.body,
  );
}

function normalizeSearch(value: string) {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("pt-BR").trim();
}
